import { ICellRender, ICols } from "../shared/table/types";
import cells from "../shared/table/cells";
import { ActionColWidth, Cells, CheckBoxColWidth } from "../shared/constants";
import HrnDeleteConfirm from "../components/HrnDeleteConfirm";
import { Checkbox, TablePaginationConfig, Typography } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

const tableUtils = {
  checkboxColumn: (onChange?: any | null, onChangeAll?: any | null) => ({
    title: <Checkbox title="Check to select all" onChange={(event) => {
      if (onChangeAll) onChangeAll(event)
    }} />,
    key: "selected",
    align: "left",
    width: CheckBoxColWidth,
    render: (_: unknown, record: any, index: number) => (
      <Checkbox checked={record.selected} onChange={(event) => {
        if (onChange) onChange(event, record, index)
      }} />
    ),
  }),
  singleActionColumn: (text?: string, onClick?: any, title?: string) => ({
    title: title || "Action",
    key: "action",
    align: "right",
    width: ActionColWidth,
    render: (_: unknown, record: any, index: number) => (
      <div className="flex justify-end gap-2.5">
        <Typography.Link onClick={() => onClick(record, index)} style={{ whiteSpace: "nowrap" }}>
          {text || "Edit"}
        </Typography.Link>
      </div>
    ),
  }),
  editDeleteActionColumns: (onEdit: any, onDelete: any, displayMode: "icon" | "text" | "icon+text" = "text") => ({
    title: "Action",
    key: "action",
    align: "right",
    width: ActionColWidth,
    render: (_: unknown, record: any, index: number) => (
      <div className="flex justify-end gap-2.5">
        <Typography.Link onClick={() => onEdit(record, index)} style={{ whiteSpace: "nowrap" }}>
          {displayMode === "text" && "Edit"}
          {displayMode === "icon" && <EditOutlined />}
          {displayMode === "icon+text" && <><EditOutlined />Edit</>}
        </Typography.Link>
        <HrnDeleteConfirm
          onDelete={() => {
            onDelete(record, index);
          }}
        >
          <Typography.Link style={{ whiteSpace: "nowrap" }}>
            {displayMode === "text" && "Delete"}
            {displayMode === "icon" && <DeleteOutlined />}
            {displayMode === "icon+text" && <><DeleteOutlined />Delete</>}
          </Typography.Link>
        </HrnDeleteConfirm>
      </div>
    ),
  }),
  editActionColumn: (onEdit: any, displayMode: "icon" | "text" | "icon+text" = "icon+text") => ({
    title: "Action",
    key: "action",
    align: "right",
    width: ActionColWidth,
    render: (_: unknown, record: any, index: number) => (
      <div className="flex justify-end gap-2.5">
        <Typography.Link onClick={() => onEdit(record, index)} style={{ whiteSpace: "nowrap" }}>
          {displayMode === "text" && "Edit"}
          {displayMode === "icon" && <EditOutlined />}
          {displayMode === "icon+text" && <><EditOutlined />Edit</>}
        </Typography.Link>
      </div>
    ),
  }),
  deleteActionColumn: (onDelete: any, displayMode: "icon" | "text" | "icon+text" = "icon+text") => ({
    title: "Action",
    key: "action",
    align: "right",
    width: ActionColWidth,
    render: (_: unknown, record: any, index: number) => (
      <div className="flex justify-end gap-2.5">
        <HrnDeleteConfirm
          onDelete={() => {
            onDelete(record, index);
          }}
        >
          <Typography.Link style={{ whiteSpace: "nowrap" }}>
            {displayMode === "text" && "Delete"}
            {displayMode === "icon" && <DeleteOutlined />}
            {displayMode === "icon+text" && <><DeleteOutlined />Delete</>}
          </Typography.Link>
        </HrnDeleteConfirm>
      </div>
    ),
  }),
  buildColumns: function buildColumns(
    data: any[],
    columns: ICols[],
    actions?: any,
    controlledColumns?: any[],
    checkboxColumn?: any,
    serialNoColumn?: any,
    selectedRowKeys?: React.Key[]
  ) {
    const cols: any[] = [];
    if (serialNoColumn) cols.push({
      title: "#",
      className: "!text-right",
      dataIndex: "index",
      key: "index",
      width: "10px",
      ...serialNoColumn,
      render: (value: any, record: any, index: number) => {
        return <span className={serialNoColumn?.className}>{((serialNoColumn?.pageNo || 1) - 1) * (serialNoColumn?.pageSize || 10) + index + 1}</span>;
      }
    });
    if (checkboxColumn) cols.push(checkboxColumn);
    const keys = data.length > 0 ? Object.keys(data[0]) : [];

    columns.forEach(
      ({
        key,
        header,
        cell,
        width,
        className,
        leftExtraText,
        rightExtraText,
        fn,
        responsive,
        ...otherProps
      }: ICols) => {
        /* Notes: keyParts is using to parse from object.  
         ex. key="invoice.invoice_no" or key="invoice.invoice_date.date" */

        const keyParts = key.includes(".") ? key.split(".") : [key];

        if (keys.includes(keyParts[0])) {
          cols.push({
            title: header,
            className,
            dataIndex: keyParts[0],
            key: keyParts[0],
            responsive,
            width,
            ...otherProps,
            render: (value: any, record: any, index: number) => {
              let cValue = value;
              if (fn) {
                cValue = fn(value, record, index);
              }

              if (!fn && keyParts.length > 1) {
                const cKparts = [...keyParts];
                cKparts.shift();
                cValue = cKparts.reduce((acc, curr) => acc && acc[curr], value);
              }
              const props: ICellRender = {
                value: cValue,
                record,
                index,
                cell,
                leftExtraText,
                rightExtraText,
                className,
              };
              return this.cellView(props);
            },
          });
        }
        else {
          cols.push({
            title: header,
            dataIndex: keyParts[0],
            key: keyParts[0],
            responsive,
            className,
            width,
            ...otherProps,
          });
        }
      },
    );

    if (actions) cols.push(actions);

    if (controlledColumns && controlledColumns.length) {
      controlledColumns.map((col: any) => {
        const { position, ...controlledCol } = col;
        return cols.splice(position, 0, controlledCol);
      });
    }

    return cols;
  },
  cellView: function cellView(props: ICellRender) {
    switch (props.cell) {
      case Cells.COMPANY_CELL:
        return cells.companyNameCell(props);
      case Cells.SURVEY_CELL:
        return cells.surveyNameCell(props);
      case Cells.APPLICATION_TYPE_CELL:
        return cells.applicationTypeNameCell(props);
      case Cells.DATE_TIME_CELL:
        return cells.dateTimeCell(props);
      case Cells.BADGE_CELL:
        return cells.badgeCell(props);
      case Cells.LENGTH_COUNT:
        return cells.listCountCell(props)
      case Cells.USER_CELL:
        return cells.userNameCell(props);
      case Cells.USER_AVATAR:
        return cells.userAvatarCell(props);
      case Cells.USER_AVATAR_NAME:
        return cells.userAvatarNameCell(props);
      default:
        return cells.textCell(props);
    }
  },
  pagination: (pageSize?: number, onPageChange?: any) => {
    return {
      pageSize: pageSize || 500,
      onChange: onPageChange,
      position: ["bottomRight"],
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100, 500]
    } as TablePaginationConfig;
  }
};

export default tableUtils;
